import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { BarStack, Bar, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  thousandFormat2,
  thousandFormat,
  getMonths2,
  getMonths,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => (d && d !== undefined ? d.month : null);
const y = (d) => (d && d !== undefined ? d.year : null);

let tooltipTimeout;

const TopChart = ({
  summaryData,
  period,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const isMountedRef = useRef(false);
  const canvasRef = useRef();

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
  } = useTooltip();

  const [state, setState] = useState({
    resize: false,
    hoverBar: null,
    selectedBars: [],
    data: [],
    totals: [],
    forecasts: [],
    colors: [],
    xTransform: 0,
    X: 0,
    Y: 0,
    iX: 0,
    iY: 0,
    rX: 0,
    rY: 0,
    rW: 0,
    rH: 0,
    canvasIndex: -1,
    dragFlag: false,
    isMouseDown: false,
  });

  const onResize = useCallback(() => {
    if (isMountedRef.current === true) {
      setState(prevState => ({ ...prevState, resize: !prevState.resize }));
    }
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictForecasts = {};
    let data = [];
    let totals = [];
    let forecasts = [];
    let yearData = {};
    let yearForecasts = {};
    let yearTotals = {};

    let colors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);
    let startDate =
      new Date(selectedYears[0] - 1, defaultStartMonth - 1, '01').getTime() +
      new Date(
        selectedYears[0] - 1,
        defaultStartMonth - 1,
        '01',
      ).getTimezoneOffset();
    let endDate =
      new Date(selectedYears[0], defaultStartMonth - 1, '01').getTime() +
      new Date(
        selectedYears[0],
        defaultStartMonth - 1,
        '01',
      ).getTimezoneOffset();

    if (period !== 'month') selectedYears = selectedYears.sort((a, b) => a - b);

    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);

    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};

      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });

      dictTotals[month] = 0;
      dictForecasts[month] = 0;
    });

    if (summaryData['byActual'])
      summaryData['byActual'].forEach((item) => {
        if (
          new Date(item.Date).getTime() >= startDate &&
          new Date(item.Date).getTime() < endDate
        ) {
          let month = getMonth(item.Date);

          if (dictData[month] !== undefined) {
            let isInFianical = fiscalMonths2.indexOf(month) > -1;

            if (!yearData[item.FY]) {
              yearTotals[item.FY] = 0;
              yearForecasts[item.FY] = 0;
              yearData[item.FY] = {};
              yearData[item.FY][item.FY] = 0;
              yearData[item.FY]['year'] = item.FY;
              yearData[item.FY]['value'] = {};
              yearData[item.FY]['value'][item.FY] = {};
              yearData[item.FY]['value'][item.FY]['ActualExpenses'] = 0;
              yearData[item.FY]['value'][item.FY]['ForecastExpenses'] = 0;
            }
            if (isInFianical) {
              dictData[month][item.FY] += parseFloat(item.ActualExpenses);
              dictTotals[month] += parseFloat(item.ActualExpenses);

              yearData[item.FY][item.FY] += parseFloat(item.ActualExpenses);
              yearTotals[item.FY] += parseFloat(item.ActualExpenses);
              yearData[item.FY]['value'][item.FY]['ActualExpenses'] +=
                parseFloat(item.ActualExpenses);
              yearForecasts[item.FY] = parseFloat(item.ForecastExpenses);
              yearData[item.FY]['value'][item.FY]['ForecastExpenses'] =
                parseFloat(item.ForecastExpenses);
            }
            dictData[month]['value'][item.FY] = item;
            dictForecasts[month] = parseFloat(item.ForecastExpenses);
          }
        }
      });

    if (period === 'month') {
      fiscalMonths.forEach((month) => {
        data.push(dictData[month]);
        totals.push(dictTotals[month]);
        forecasts.push(dictForecasts[month]);
      });
    } else {
      selectedYears.forEach((year) => {
        if (yearData[year]) {
          data.push(yearData[year]);
          totals.push(yearTotals[year]);
          forecasts.push(yearForecasts[year]);
        }
      });
    }

    let xTransform = 0;
    let maxYValue = Math.max(Math.max(...totals), Math.max(...forecasts));
    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      xTransform = 30;
    else {
      if (parseInt(thousandFormat(maxYValue)[0]) > 7)
        xTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 5;
      else xTransform = 7.5 * thousandFormat2(maxYValue).length + 5;
    }

    setState(prevState => ({
      ...prevState,
      data,
      totals,
      forecasts,
      colors,
      xTransform,
    }));
  }, [summaryData, selectedYears, defaultStartMonth, period, defaultMonth]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    const actual = bar.bar.data.value[year]
      ? bar.bar.data.value[year].ActualExpenses
      : 0;
    const forecast = bar.bar.data.value[year]
      ? bar.bar.data.value[year].ForecastExpenses
      : 0;

    const activeColor =
      parseFloat(actual) > parseFloat(forecast)
        ? negativeActiveColor
        : positiveActiveColor;
    const disableColor =
      parseFloat(actual) > parseFloat(forecast)
        ? negativeDisableColor
        : positiveDisableColor;

    if (selectedTopItems.length === 0 && selectedMonths.length === 0) {
      return activeColor;
    }

    if (selectedTopItems.length === 0) {
      if (month !== undefined && selectedMonths.indexOf(month) > -1)
        return activeColor;
    }

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        (selectedTopItems[i].month === month ||
          selectedTopItems[i].month === undefined) &&
        selectedTopItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedMonths, selectedTopItems]);

  const getMousePositionOnCanvas = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const drawRectangle = (end = false) => {
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.lineWidth = '1';
    context.strokeStyle = '#00f3ff';
    context.rect(state.rX, state.rY, state.rW, state.rH);
    context.stroke();
    context.globalAlpha = 0.2;
    context.fillStyle = 'lightblue';
    context.fill();
    if (end) {
      drawSelectBar();
    }
  };

  const resetCanvas = () => {
    setState(prevState => ({
      ...prevState,
      X: 0,
      Y: 0,
      iX: 0,
      iY: 0,
      rX: 0,
      rY: 0,
      rW: 0,
      rH: 0,
      canvasIndex: -1,
      dragFlag: false,
    }));
    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    context.beginPath();
  };

  const drawSelectBar = () => {
    const { X, iX, rW, rH, xTransform } = state;
    const fiscalMonths = getMonths(defaultStartMonth);
    const unitWidth = (window.innerWidth - 16 - xTransform) / fiscalMonths.length;
    const startPosition = X >= iX ? iX - xTransform : X - xTransform;
    const endPosition = X >= iX ? X - xTransform : iX - xTransform;
    const maxMonthIndex = fiscalMonths.length - 1;
    const startMonth = Math.max(
      0,
      Math.min(maxMonthIndex, Math.floor(startPosition / unitWidth)),
    );
    const endMonth = Math.max(
      0,
      Math.min(maxMonthIndex, Math.floor(endPosition / unitWidth)),
    );
    const hasDragged = Math.abs(rW) > 2 || Math.abs(rH) > 2;
    let _selectedTopItems = [],
      _selectedMonths = [];

    for (let i = startMonth; i <= endMonth; i++) {
      const _month = fiscalMonths[i];
      _selectedTopItems.push({ month: _month, year: selectedYears[0] });
      _selectedMonths.push(_month);
    }

    if (_selectedTopItems.length > 0) {
      if (hasDragged) {
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
        });
      } else {
        handleFilter({
          selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
          selectedMonths: [_selectedMonths[_selectedMonths.length - 1]],
        });
      }
    }

    resetCanvas();
  };

  const onMouseMove = (event) => {
    if (!state.dragFlag || !canvasRef.current) return;

    const pos = getMousePositionOnCanvas(event);

    setState(prevState => ({
      ...prevState,
      X: pos.x,
      Y: pos.y,
      rX: Math.min(prevState.iX, pos.x),
      rY: Math.min(prevState.iY, pos.y),
      rW: prevState.iX ? Math.abs(prevState.iX - pos.x) : 0,
      rH: prevState.iY ? Math.abs(prevState.iY - pos.y) : 0,
    }));

    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    drawRectangle();
  };

  const onMouseOut = (event) => {
    if (state.iX && state.iY) {
      // drawRectangle(true);
    }
  };

  const endDraw = () => {
    if (state.iX && state.iY) {
      drawRectangle(true);
    }
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !canvasRef.current) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true')
    ) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      isMouseDown: true,
    }));

    if (state.dragFlag) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setState(prevState => ({
      ...prevState,
      canvasIndex: 0,
      X: startX,
      Y: startY,
      iX: startX,
      iY: startY,
      rX: startX,
      rY: startY,
      rW: 0,
      rH: 0,
      dragFlag: true,
    }));
  };

  const onSvgMouseMove = (event) => {
    if (!state.isMouseDown || state.dragFlag || event.buttons !== 1 || !canvasRef.current)
      return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setState(prevState => ({
      ...prevState,
      canvasIndex: 0,
      X: startX,
      Y: startY,
      iX: startX,
      iY: startY,
      rX: startX,
      rY: startY,
      rW: 0,
      rH: 0,
      dragFlag: true,
    }));
  };

  const onSvgMouseUp = () => {
    setState(prevState => ({
      ...prevState,
      isMouseDown: false,
    }));
  };

  const onSvgMouseLeave = () => {
    setState(prevState => ({
      ...prevState,
      isMouseDown: false,
    }));
  };

  const handleBar = useCallback((event, bar) => {
    if (state.dragFlag) return;
    const { selectedBars } = state;

    let _selectedBars = [],
      _selectedMonths = [],
      _selectedTopItems = [];
    let _selectedYears = selectedYears.slice();
    const month = bar.bar.data.month;

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      let _selectedBars = selectedBars.slice();
      let _selectedMonths = selectedMonths.slice();
      let _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);

        if (period === 'month') {
          _selectedTopItems.push({ month: month, year: selectedYears[0] });
        } else {
          _selectedTopItems.push({ month: undefined, year: month });
        }
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }

      setState(prevState => ({
        ...prevState,
        selectedBars: _selectedBars,
      }));
      handleFilter({
        selectedTopItems: _selectedTopItems,
        selectedMonths: _selectedMonths,
      });
    } else {
      _selectedMonths = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      _selectedMonths = [month];
      if (period === 'month') {
        _selectedYears.forEach((year) => {
          _selectedTopItems.push({ year: year, month: month });
        });
      } else {
        _selectedTopItems = [{ year: month, month: undefined }];
      }
      _selectedBars = [event.target];
      event.target.classList.add('barActive');

      setState(prevState => ({
        ...prevState,
        selectedBars: _selectedBars,
      }));
  
      handleFilter({
        selectedTopItems: _selectedTopItems,
        selectedMonths: _selectedMonths,
      });
    }
  }, [
    state.selectedBars,
    selectedYears,
    selectedMonths,
    selectedTopItems,
    period,
    handleFilter,
    state.dragFlag,
  ]);

  const handleLabel = useCallback((event, month) => {
    const { selectedBars } = state;
    let _selectedMonths, _selectedTopItems, _selectedBars;
    let _selectedYears = selectedYears.slice();
    const allIndex = _selectedYears.indexOf('All');
    if (allIndex > -1) _selectedYears.splice(allIndex, 1);

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMonths = selectedMonths.slice();
      _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        if (period === 'month') {
          _selectedYears.forEach((year) => {
            _selectedTopItems.push({ year: year, month: month });
          });
        } else {
          _selectedTopItems.push({ year: month, month: undefined });
        }
      } else {
        _selectedMonths.splice(index, 1);
        if (period === 'month')
          _selectedTopItems.splice(index, _selectedYears.length);
        else _selectedTopItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      _selectedMonths = [month];
      if (period === 'month') {
        _selectedYears.forEach((year) => {
          _selectedTopItems.push({ year: year, month: month });
        });
      } else {
        _selectedTopItems = [{ year: month, month: undefined }];
      }
    }

    setState(prevState => ({
      ...prevState,
      selectedBars: _selectedBars,
      selectedTopItems: _selectedTopItems,
    }));

    handleFilter({
      selectedMonths: _selectedMonths,
      selectedTopItems: _selectedTopItems,
    });
  }, [state.selectedBars, selectedYears, selectedMonths, selectedTopItems, defaultStartMonth, period, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {};
    const coords = localPoint(event.target.ownerSVGElement, event);

    if (isBar) {
      const { hoverBar } = state;
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setState(prevState => ({ ...prevState, hoverBar: event.target }));
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    // calculate mtdSum
    let mtdSum = 0;
    let mtdMonths = 0;
    if (summaryData['byActual'] && selectedTopItems.length > 1)
      summaryData['byActual'].forEach((item) => {
        let month = getMonth(item.Date);
        selectedTopItems.forEach((sel) => {
          if (month === sel.month) {
            mtdSum += item.ActualExpenses;
            mtdMonths++;
          }
        });
      });

    data['mtdSum'] = mtdSum;
    data['items'] = mtdMonths;

    showTooltip({
      tooltipData: data,
      tooltipTop: coords.y + 20,
      tooltipLeft: coords.x,
    });
  }, [state, summaryData, selectedTopItems, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    const { hoverBar } = state;

    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setState(prevState => ({ ...prevState, hoverBar: null }));

      hideTooltip();
    }, 300);
  }, [state, hideTooltip]);

  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  useEffect(() => {
    if (isMountedRef.current === true) {
      prepareData();
    }
  }, [summaryData, period, prepareData]);

  const {
    data,
    totals,
    forecasts,
    colors,
    xTransform,
    canvasIndex,
  } = state;

  const width = window.innerWidth - 16;
  const height = window.innerHeight / 6.5;
  const margin = {
    top: 5,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  const offsetWidth = -1.2;

  // scales
  const xScale = period === 'month'
    ? scaleBand()
      .domain(data.map(x))
      .padding(0.2)
      .rangeRound([0, xMax])
    : scaleBand()
      .domain(data.map(y))
      .padding(0.2)
      .rangeRound([0, xMax])

  let yMin =
    Math.min(Math.min(...totals), Math.min(...forecasts)) > 0
      ? 0
      : Math.min(Math.min(...totals) * 1.2, Math.min(...forecasts)) * 1.2;
  const yScale = scaleLinear({
    domain: [
      yMin,
      Math.max(Math.max(...totals) * 1.2, Math.max(...forecasts)) * 1.2,
    ],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);
  let bottomItems = period === 'month' ? data.map(x) : data.map(y);

  return (
    <div id="expense-top-chart" style={{ height: 168 }}>
      <div className="subtitle mb-10">Actual vs Forecast</div>

      <Grid container>
        <canvas
          width={width}
          height={height - 20}
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={endDraw}
          onMouseOut={onMouseOut}
          style={{ position: 'fixed', zIndex: canvasIndex }}
        />
        <svg
          width={width}
          height={height}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
        >
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
          />

          <Group top={margin.top} left={margin.left}>
            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.2em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />
            <BarStack
              data={data}
              keys={selectedYears}
              x={period === 'month' ? x : y}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={bar.x}
                        y={
                          bar.y
                            ? bar.height >= 0
                              ? bar.y
                              : bar.y - Math.abs(bar.height)
                            : 0
                        }
                        height={isNaN(bar.height) ? 0 : Math.abs(bar.height)}
                        width={isNaN(bar.width) ? 0 : bar.width}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={(event) => hideTooltipHandler()}
                        onMouseMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                        onTouchEnd={(event) => hideTooltipHandler()}
                        onTouchMove={(event) =>
                          showTooltipHandler(event, xScale, bar)
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>
            <LinePath
              data={[
                { x: 0, y: yScale(0) },
                { x: width, y: yScale(0) },
              ]}
              x={(d) => (d.x ? d.x : 0)}
              y={(d) => (d.y ? d.y : 0)}
              stroke="#555555"
            />

            {forecasts.map((forecast, index) => {
              return (
                <Group key={`bar-forecast-${index}`} left={0}>
                  <Bar
                    width={
                      (xMax - xScale.paddingInner() * xScale.step()) /
                        (period === 'month'
                          ? fiscalMonths.length
                          : selectedYears.length) +
                      offsetWidth
                    }
                    height={1}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step() +
                      offsetX
                    }
                    y={yScale(forecast) ? yScale(forecast) : 0}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={(event) => hideTooltipHandler()}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                    onTouchEnd={(event) => hideTooltipHandler()}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                  />
                </Group>
              );
            })}

            {bottomItems.map((oneItem, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax ? yMax : 0}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                    (period === 'month'
                      ? bottomItems.length
                      : selectedYears.length)
                  }
                  height={margin.bottom}
                  fill="transparent"
                  data-no-drag="true"
                  onClick={(event) => handleLabel(event, oneItem)}
                />
              );
            })}

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedMonths.indexOf(parseInt(formattedValue)) > -1
                        ? 'bold'
                        : 500,
                  }}
                  fill={
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {period === 'month'
                    ? enMonths[formattedValue]
                    : formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="color-black" style={{ padding: 0, margin: 0 }}>
                {tooltipData.mtdSum > 0 ? (
                  <div
                    className="ft-12"
                    style={{
                      backgroundColor: '#dddddd',
                      margin: 0,
                      minHeight: 25,
                    }}
                  >
                    <div style={{ padding: 5 }}>
                      {tooltipData.items} items selected SUM(ExpenseMTD
                      HistoricalOnly):
                      <strong>{thousandFormat2(tooltipData.mtdSum)}</strong>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                <div style={{ margin: 5 }}>
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[x(tooltipData.bar.data)]}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Actual:{' '}
                    <strong>
                      {thousandFormat2(tooltipData.bar.data[tooltipData.key])}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Forecast:{' '}
                    <strong>
                      {thousandFormat2(
                        tooltipData.bar.data.value[tooltipData.key]
                          .ForecastExpenses,
                      )}
                    </strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="color-black" style={{ margin: 5 }}>
                Sum ExpenseMTDForecast HistoricalOnly ={' '}
                {thousandFormat(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.object.isRequired,
  period: PropTypes.string.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
